import { API_SECRET, APP_VERSION, DEVICE_REGISTRY_URL, isNativeShell } from "../config/appConfig";
import { getDeviceIdAsync } from "./deviceStorage";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

function detectStandalonePwa() {
  try {
    if (window?.matchMedia?.("(display-mode: standalone)")?.matches) return true;
    if (window?.navigator?.standalone) return true;
  } catch (_) {}
  return false;
}

export function detectDeviceRuntime() {
  const ua = String(window?.navigator?.userAgent || "");
  const lower = ua.toLowerCase();
  const nativeShell = isNativeShell();
  const standalone = detectStandalonePwa();
  const platform = /android/.test(lower)
    ? "android"
    : /iphone|ipad|ipod/.test(lower)
      ? "ios"
      : /mac os|macintosh/.test(lower)
        ? "macos"
        : /windows/.test(lower)
          ? "windows"
          : /linux/.test(lower)
            ? "linux"
            : "web";
  const container = nativeShell ? "native" : standalone ? "pwa" : "browser";
  const pushProvider = container === "native" && platform === "ios"
    ? "apns"
    : container === "native" && platform === "android"
      ? "fcm"
      : "none";
  const supportsLiveActivities = platform === "ios" && container === "native";
  const supportsPush = container === "native" && (pushProvider === "apns" || pushProvider === "fcm");
  return {
    platform,
    container,
    pushProvider,
    supportsSync: true,
    supportsLiveActivities,
    supportsPush,
    verificationRequired: container === "native",
    userAgent: ua,
    appVersion: APP_VERSION,
    isNativeShell: nativeShell,
  };
}

function sessionIdentity(session = {}) {
  return {
    accountId: session?.accountId || session?.id || "",
    userId: session?.userId || "",
    username: session?.username || "",
    userName: session?.name || session?.userName || session?.username || "",
    sessionId: session?.sessionId || session?.loginId || session?.id || "",
    role: session?.role || session?.userRole || session?.profile || "",
  };
}

// v3.2.08: Admin e Gestor usam registro global único em vez de por fazenda.
// Reduz chamadas a device-registry.php para multi-fazenda e elimina
// aprovação redundante do mesmo device em cada fazenda.
function isAdminOrGestorSession(session = {}) {
  const role = String(
    session?.role || session?.userRole || session?.profile || ""
  ).toLowerCase().trim();
  return ["admin", "administrador", "gestor", "manager"].includes(role);
}

function effectiveRegistryFarmId(farmId, session) {
  return isAdminOrGestorSession(session)
    ? "__admin_global__"
    : String(farmId || "").trim();
}


const registryInFlight = new Map();

function generateVerificationCode() {
  try {
    const arr = new Uint32Array(1);
    window.crypto?.getRandomValues?.(arr);
    return String(arr[0] % 1000000).padStart(6, "0");
  } catch (_) {
    return String(Math.floor(Math.random() * 1000000)).padStart(6, "0");
  }
}

async function sha256Hex(text) {
  const input = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest("SHA-256", input);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function buildLocalVerificationPayload({ farmId, deviceId, runtime, verificationStoreKey }) {
  if (!runtime?.verificationRequired) return { localVerification: null, verificationHash: "", verificationHint: "" };
  let localVerification = null;
  try {
    const cached = JSON.parse(localStorage.getItem(verificationStoreKey) || "null");
    if (cached?.code && cached?.farmId === farmId && cached?.deviceId === deviceId && cached?.expiresAt) {
      const expiresTs = new Date(cached.expiresAt).getTime();
      if (Number.isFinite(expiresTs) && expiresTs > Date.now()) localVerification = cached;
    }
  } catch (_) {}
  if (!localVerification) {
    const code = generateVerificationCode();
    localVerification = {
      deviceId,
      farmId,
      code,
      hint: code.slice(-3),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      platform: runtime.platform,
      container: runtime.container,
      receivedAt: new Date().toISOString(),
      source: "local-native-verification",
    };
    try { localStorage.setItem(verificationStoreKey, JSON.stringify(localVerification)); } catch (_) {}
  }
  const verificationHash = await sha256Hex(`${localVerification.code}|${farmId}|${deviceId}|${API_SECRET}`);
  return { localVerification, verificationHash, verificationHint: localVerification.hint || localVerification.code.slice(-3) };
}


const approvalCheckInFlight = new Map();

export async function checkNativeDeviceApproval({ farmId, session } = {}) {
  const fid = effectiveRegistryFarmId(farmId, session);
  if (!fid || !session) return { ok: false, error: "missing_context" };
  const runtime = detectDeviceRuntime();
  if (!runtime?.verificationRequired) return { ok: true, skipped: true, reason: "verification_not_required" };
  const identity = sessionIdentity(session);
  const deviceId = await getDeviceIdAsync();
  const userKey = identity.accountId || identity.username || identity.userId || "user";
  const cooldownKey = `fz:device-registry:approval-check:last:${fid}:${userKey}:${deviceId}`;
  const inFlightKey = `${fid}:${userKey}:${deviceId}`;
  const now = Date.now();
  try {
    const last = Number(localStorage.getItem(cooldownKey) || 0);
    if (last && now - last < 2 * 60 * 1000) {
      return { ok: false, throttled: true, retryAfterMs: Math.max(0, 2 * 60 * 1000 - (now - last)) };
    }
    localStorage.setItem(cooldownKey, String(now));
  } catch (_) {}
  if (approvalCheckInFlight.has(inFlightKey)) return approvalCheckInFlight.get(inFlightKey);
  const requestPromise = (async () => {
    try {
      const res = await traceAndFetch(DEVICE_REGISTRY_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_SECRET,
          "X-Device-ID": deviceId,
          "X-User-Name": identity.userName || identity.username || "unknown",
        },
        body: JSON.stringify({
          action: "check_status",
          farm_id: fid,
          account_id: identity.accountId,
          user_id: identity.userId,
          username: identity.username,
          user_name: identity.userName,
          device_id: deviceId,
          platform: runtime.platform,
          container: runtime.container,
          push_provider: runtime.pushProvider,
        }),
      }, { source: "device-registry.checkStatus", action: "check_status" });
      const data = await res.json().catch(() => null);
      if (res.status === 429) {
        try { localStorage.setItem(cooldownKey, String(Date.now() + 3 * 60 * 1000)); } catch (_) {}
      }
      const info = data?.data || {};
      if (res.ok && data?.ok && (info.device_status === "verified" || info.device_status === "active")) {
        const verificationStoreKey = `fz:nativeDeviceVerification:${fid}:${deviceId}`;
        window.__fzNativeDeviceVerification = null;
        try { localStorage.removeItem(verificationStoreKey); } catch (_) {}
        window.dispatchEvent(new CustomEvent("fz:device-verification-code", { detail: null }));
      }
      return { ok: res.ok && !!data?.ok, status: res.status, data };
    } catch (error) {
      try { localStorage.removeItem(cooldownKey); } catch (_) {}
      return { ok: false, error: error?.message || "device_approval_check_failed" };
    }
  })();
  approvalCheckInFlight.set(inFlightKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    approvalCheckInFlight.delete(inFlightKey);
  }
}

export async function registerUniversalDeviceSession({ farmId, session, reason = "dashboard" } = {}) {
  const fid = effectiveRegistryFarmId(farmId, session);
  if (!fid || !session) return null;
  const runtime = detectDeviceRuntime();
  const identity = sessionIdentity(session);
  const deviceId = await getDeviceIdAsync();
  const throttleKey = `fz:device-registry:last:${fid}:${identity.accountId || identity.username || identity.userId || "user"}:${deviceId}`;
  const verificationStoreKey = `fz:nativeDeviceVerification:${fid}:${deviceId}`;
  try {
    const last = Number(localStorage.getItem(throttleKey) || 0);
    if (last && Date.now() - last < 10 * 60 * 1000) {
      try {
        const cached = JSON.parse(localStorage.getItem(verificationStoreKey) || "null");
        if (cached?.code) {
          window.__fzNativeDeviceVerification = cached;
          window.dispatchEvent(new CustomEvent("fz:device-verification-code", { detail: cached }));
        }
      } catch (_) {}
      return { skipped: true, reason: "throttled" };
    }
    localStorage.setItem(throttleKey, String(Date.now()));
  } catch (_) {}
  const inFlightKey = `${fid}:${identity.accountId || identity.username || identity.userId || "user"}:${deviceId}`;
  if (registryInFlight.has(inFlightKey)) return registryInFlight.get(inFlightKey);
  const { localVerification, verificationHash, verificationHint } = await buildLocalVerificationPayload({ farmId: fid, deviceId, runtime, verificationStoreKey });
  const payload = {
    action: "register",
    farm_id: fid,
    account_id: identity.accountId,
    user_id: identity.userId,
    username: identity.username,
    user_name: identity.userName,
    session_id: identity.sessionId,
    device_id: deviceId,
    platform: runtime.platform,
    container: runtime.container,
    push_provider: runtime.pushProvider,
    app_version: runtime.appVersion,
    user_agent: runtime.userAgent,
    supports_sync: runtime.supportsSync,
    supports_live_activities: runtime.supportsLiveActivities,
    supports_push: runtime.supportsPush,
    verification_required: runtime.verificationRequired,
    verification_code_hash: verificationHash,
    verification_code_hint: verificationHint,
    is_native_shell: runtime.isNativeShell,
    reason,
  };
  const requestPromise = (async () => {
  try {
    const res = await traceAndFetch(DEVICE_REGISTRY_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": API_SECRET,
        "X-Device-ID": deviceId,
        "X-User-Name": identity.userName || identity.username || "unknown",
      },
      body: JSON.stringify(payload),
    }, { source: "device-registry.register", action: "register" });
    const data = await res.json().catch(() => null);
    const result = { ok: res.ok && !!data?.ok, status: res.status, data };
    try {
      const info = data?.data || {};
      if (info?.verification_required && info?.device_status === "pending" && localVerification?.code) {
        window.__fzNativeDeviceVerification = {
          ...localVerification,
          hint: String(info.verification_code_hint || localVerification.hint || ""),
          expiresAt: info.verification_code_expires_at || localVerification.expiresAt || "",
        };
        try { localStorage.setItem(verificationStoreKey, JSON.stringify(window.__fzNativeDeviceVerification)); } catch (_) {}
        window.dispatchEvent(new CustomEvent("fz:device-verification-code", { detail: window.__fzNativeDeviceVerification }));
      } else if (info?.device_status === "verified" || info?.device_status === "active") {
        window.__fzNativeDeviceVerification = null;
        try { localStorage.removeItem(verificationStoreKey); } catch (_) {}
        window.dispatchEvent(new CustomEvent("fz:device-verification-code", { detail: null }));
      }
    } catch (_) {}
    return result;
  } catch (error) {
    try { localStorage.removeItem(throttleKey); } catch (_) {}
    return { ok: false, error: error?.message || "device_registry_failed" };
  }
  })();
  registryInFlight.set(inFlightKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    registryInFlight.delete(inFlightKey);
  }
}
